import type { Vec3 } from '../../geo/types';
import type { Frame, MeshBuilder, VertexStyle } from '../mesh/meshBuilder';
import { timberUv } from '../streetscape/timber';

/*
 * An open timber railing, as the 1544 woodcut draws the long bridges (woodcut_oudebrug and
 * woodcut_papenbrug in docs/plans/streetscape.md): square posts between a bottom and a top rail, the
 * panels between them open. The woodcut sets the proportions: posts stand 1.4 railing heights apart, and
 * each rail is 0.15 of the railing height deep. The height and thickness are the bridges' earlier rail.
 */
export const RAILING = {
  height: 0.8,
  thickness: 0.15,
  postSpacingShare: 1.4,
  railShare: 0.15,
} as const;

/** The deck's height along a side, from its start. */
export type HeightAlong = (x: number) => number;

/**
 * A solid in a side's frame (x along the side, y up, z off the deck), one thickness deep: its bottom and
 * top run straight between the heights they take at each of `xs`.
 */
export interface RailingSolid {
  readonly xs: readonly number[];
  readonly bottom: readonly number[];
  readonly top: readonly number[];
}

export interface RailingParts {
  readonly rails: readonly RailingSolid[];
  readonly posts: readonly RailingSolid[];
}

const LEVEL: HeightAlong = () => 0;
const COLLINEAR = 1e-9;

// Post middles, evenly spaced from end to end and no further apart than the woodcut's; a side too
// short for two posts takes one in its middle, so that no two posts overlap.
function postMiddles(length: number): number[] {
  const width = RAILING.thickness;
  if (length < width) return [];
  if (length < 2 * width) return [length / 2];
  const run = length - width;
  const gaps = Math.ceil(run / (RAILING.postSpacingShare * RAILING.height));
  return Array.from({ length: gaps + 1 }, (_, k) => width / 2 + (k * run) / gaps);
}

// The rails bend halfway between posts, so each post stands on one straight piece of them.
function railBreaks(
  length: number,
  posts: readonly number[],
  along: HeightAlong,
  knots: readonly number[]
): number[] {
  const breaks = [
    ...new Set([
      0,
      ...posts.slice(1).map((x, k) => (posts[k]! + x) / 2),
      ...knots.filter((x) => x > 1e-5 && x < length - 1e-5),
      length,
    ]),
  ].sort((a, b) => a - b);
  return breaks.filter((x, k) => {
    if (k === 0 || k === breaks.length - 1 || knots.includes(x)) return true;
    const [a, b] = [breaks[k - 1]!, breaks[k + 1]!];
    const slopeIn = (along(x) - along(a)) / (x - a);
    const slopeOut = (along(b) - along(x)) / (b - x);
    return Math.abs(slopeIn - slopeOut) > COLLINEAR;
  });
}

function lerp(xs: readonly number[], ys: readonly number[], x: number): number {
  const next = xs.findIndex((b) => b > x);
  const k = next === -1 ? xs.length - 2 : Math.max(0, next - 1);
  const t = (x - xs[k]!) / (xs[k + 1]! - xs[k]!);
  return ys[k]! + t * (ys[k + 1]! - ys[k]!);
}

/** The rails and posts along a deck side `length` long, over the deck's height along it. */
export function railingParts(
  length: number,
  along: HeightAlong = LEVEL,
  knots: readonly number[] = []
): RailingParts {
  if (length <= 0) return { rails: [], posts: [] };
  const { height, thickness } = RAILING;
  const rail = RAILING.railShare * height;
  const middles = postMiddles(length);
  const xs = railBreaks(length, middles, along, knots);
  const deck = xs.map(along);
  const raised = (dy: number) => deck.map((y) => y + dy);
  const posts = middles.map((x): RailingSolid => {
    const ends = [x - thickness / 2, x + thickness / 2];
    const base = ends.map((e) => lerp(xs, deck, e));
    return { xs: ends, bottom: base.map((y) => y + rail), top: base.map((y) => y + height - rail) };
  });
  return {
    rails: [
      { xs, bottom: deck, top: raised(rail) },
      { xs, bottom: raised(height - rail), top: raised(height) },
    ],
    posts,
  };
}

// One closed solid: a sheared box per straight piece, with end faces only at the solid's two ends.
function buildSolid(builder: MeshBuilder, style: VertexStyle, frame: Frame, s: RailingSolid): void {
  const z = [0, RAILING.thickness] as const;
  const at = (k: number, y: readonly number[], zk: number): Vec3 => [s.xs[k]!, y[k]!, zk];
  const face = (corners: readonly [Vec3, Vec3, Vec3, Vec3]): void => {
    builder.quad(style, frame, corners, timberUv(corners));
  };
  for (let k = 0; k + 1 < s.xs.length; k++) {
    const [a, b] = [k, k + 1];
    face([
      at(a, s.bottom, z[0]),
      at(b, s.bottom, z[0]),
      at(b, s.bottom, z[1]),
      at(a, s.bottom, z[1]),
    ]);
    face([at(a, s.top, z[1]), at(b, s.top, z[1]), at(b, s.top, z[0]), at(a, s.top, z[0])]);
    face([at(a, s.top, z[0]), at(b, s.top, z[0]), at(b, s.bottom, z[0]), at(a, s.bottom, z[0])]);
    face([at(b, s.top, z[1]), at(a, s.top, z[1]), at(a, s.bottom, z[1]), at(b, s.bottom, z[1])]);
  }
  const last = s.xs.length - 1;
  face([
    at(last, s.top, z[0]),
    at(last, s.top, z[1]),
    at(last, s.bottom, z[1]),
    at(last, s.bottom, z[0]),
  ]);
  face([at(0, s.top, z[1]), at(0, s.top, z[0]), at(0, s.bottom, z[0]), at(0, s.bottom, z[1])]);
}

export function buildRailing(
  builder: MeshBuilder,
  style: VertexStyle,
  frame: Frame,
  length: number,
  along: HeightAlong = LEVEL,
  knots: readonly number[] = []
): void {
  const { rails, posts } = railingParts(length, along, knots);
  for (const solid of [...rails, ...posts]) buildSolid(builder, style, frame, solid);
}
